/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * Licensed under The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { ComponentChildren, Fragment } from 'preact';
import { ItemContext } from '../UNSAFE_Collection';
import { Collection } from '../PRIVATE_Collection';
import { useViewportIntersect, ViewportConfig } from '../hooks/UNSAFE_useViewportIntersect';

/**
 * Props for the LoadMoreCollection component
 */
type Props<D> = {
  /**
   * A DataState object that provide information including data and metadata to the LoadMoreCollection.
   */
  data: D[] | null;

  /**
   * Renderer callback used to render each item
   */
  children: (context: ItemContext<D>) => ComponentChildren;

  /**
   * Viewport configuration including specifying the element which is used to determine what range
   * of data to render
   */
  viewportConfig: ViewportConfig;

  /**
   * Whether there are still more data to load.  Internally, LoadMoreCollection will render the load
   * more indicator at the bottom if this is true
   */
  hasMore: boolean;

  /**
   * Callback function to handle when user scrolls to the end of the component and there are more items to load.
   * The function should update data prop on the component to include the next set of items fetched.
   */
  onLoadMore: () => void;

  /**
   * Optionally specify the custom load more indicator
   */
  loadMoreIndicator?: ComponentChildren;

  /**
   * Optionally provide the suggestion indicator sparkle
   */
  suggestions?: ComponentChildren;

  /**
   * Optionally specify how far in pixel from the bottom should load more occurs
   */
  loadMoreThreshold?: number;
};

export const LOADMORE_STYLE_CLASS = 'oj-collection-loadmore';

const DEFAULT_MARGIN = 1;
const LOAD_MORE_SELECTOR = `.${LOADMORE_STYLE_CLASS}`;

/**
 * Component that only render items in the specified viewport.
 * @param props
 */
export function LoadMoreCollection<D>({
  data,
  children,
  viewportConfig,
  hasMore,
  onLoadMore,
  loadMoreIndicator,
  suggestions,
  loadMoreThreshold = DEFAULT_MARGIN
}: Props<D>) {
  // detect if viewport has changed and it needs to re-render based on a new range
  useViewportIntersect(
    viewportConfig,
    Math.max(loadMoreThreshold, DEFAULT_MARGIN),
    0,
    LOAD_MORE_SELECTOR,
    () => {
      if (data) {
        onLoadMore();
      }
    }
  );

  // if DataState is invalid (including not specified), just render blank
  if (!data) {
    return <div />;
  }

  // might need to support elementType prop in the future to allow customization of what
  // type of element to render for the root
  return (
    <Fragment>
      {suggestions}
      <Collection items={data}>{children}</Collection>
      {hasMore && loadMoreIndicator}
    </Fragment>
  );
}
